import { httpsCallable } from "firebase/functions";
import { functions } from "../FirebaseConfig";
import { apiLogger } from "../services/LoggingService";

/**
 * Create a new business listing
 * @param {Object} listingData - The listing data
 * @returns {Promise<Object>} - The created listing
 */
export const createBusinessListing = async (listingData) => {
  const createListing = httpsCallable(functions, "createBusinessListing");
  try {
    apiLogger.info("Creating business listing", { name: listingData.name });
    const result = await createListing(listingData);
    apiLogger.trackAction("business_listing_created", { 
      listingId: result.data?.id 
    });
    return result.data;
  } catch (error) {
    apiLogger.error("Failed to create business listing", error, { 
      listingName: listingData.name 
    });
    throw error;
  }
};

/**
 * Update an existing business listing
 * @param {string} id - The listing ID
 * @param {Object} updates - The updates to apply
 * @returns {Promise<Object>} - The updated listing
 */
export const updateBusinessListing = async (id, updates) => {
  const updateListing = httpsCallable(functions, "updateBusinessListing");
  try {
    apiLogger.info("Updating business listing", { id });
    const result = await updateListing({ id, ...updates });
    apiLogger.trackAction("business_listing_updated", { listingId: id });
    return result.data;
  } catch (error) {
    apiLogger.error("Failed to update business listing", error, { 
      listingId: id 
    });
    throw error;
  }
};

/**
 * Delete (soft delete) a business listing
 * @param {string} id - The listing ID
 * @returns {Promise<Object>} - Confirmation of deletion
 */
export const deleteBusinessListing = async (id) => {
  const deleteListing = httpsCallable(functions, "deleteBusinessListing");
  try {
    apiLogger.info("Deleting business listing", { id });
    const result = await deleteListing({ id });
    apiLogger.trackAction("business_listing_deleted", { listingId: id });
    return result.data;
  } catch (error) {
    apiLogger.error("Failed to delete business listing", error, { 
      listingId: id 
    });
    throw error;
  }
};

/**
 * Fetch all business listings
 * @returns {Promise<Array>} - Array of business listings
 */
export const fetchBusinessListings = async () => {
  const fetchListings = httpsCallable(functions, "fetchBusinessListings");
  try {
    apiLogger.debug("Fetching business listings");
    const result = await fetchListings();
    apiLogger.debug("Business listings fetched", { 
      count: result.data?.length || 0 
    });
    return result.data;
  } catch (error) {
    apiLogger.error("Failed to fetch business listings", error);
    throw error;
  }
};
